import type {
  DefaultSearchParams,
  DefaultSearchParamsByTravelType,
  TravelType,
} from '@/modules/common/context/QueryParams/models';
import type {LooseString} from '@/types';

import {getSearchFormDefaultValues} from '@travel-frontend/ui-components';

import {config} from '@/config/generated';
import {mapFormValuesToSearchParams} from '@/modules/common/components/SearchBar/utils';
import {TravelTypes} from '@/modules/common/context/QueryParams/models';

const getDefaultSearchParams = (
  travelType: TravelType
): DefaultSearchParams => {
  const defaultSearchParams = {
    ...mapFormValuesToSearchParams(
      getSearchFormDefaultValues({
        airports: config.airports,
        travelPeriod: {
          period: config.travelPeriod.period,
          duration: config.travelPeriod.duration[travelType],
        },
      })
    ),
    travelType,
  } as DefaultSearchParamsByTravelType[typeof travelType]; // @FIXME

  return {
    ...defaultSearchParams,
    // mapper only creates "rooms" as they are the default going forward.
    // for query parameter parsing however, we need to have the adults and children as well
    adults: defaultSearchParams.rooms.adultsCount,
    children: defaultSearchParams.rooms.childrenAges,
  };
};

// @TODO copied this for now from the useSearchFormDefaultConfig()-hook...
export const searchParamLimits = {
  maxAdults: 7,
  maxChildren: 6,
  maxTravellers: 8,
  maxRooms: 4,
} as const;

export const defaultSearchParamsByTravelType = {
  [TravelTypes.packageTour]: getDefaultSearchParams(TravelTypes.packageTour),
  [TravelTypes.ownArrival]: getDefaultSearchParams(TravelTypes.ownArrival),
} as const;

// @TODO move to json
export const defaultTravelType = TravelTypes.packageTour;

/**
 * List of all used splits inside find/app
 * This list powers our DebugDrawer to easily enable/disable splits
 * But also helps us autocomplete available splits when using the useLocalSplit
 */
export const splits = {
  EXP_5_pdt_cross_border_selling: ['off', 'on'],
  EXP_0001_pdt_payment_icons: ['off', 'on'],
  FFP_4042_service_center_banner: [
    'off',
    'enhancedAgentPicture',
    'agentPicture',
  ],
  FFP_3399_offers_pricechart: [
    'off',
    'expanded',
    'collapsed',
    'expanded_old',
    'expanded_new',
    'remove_tooltip',
  ],
  'FSE-2710_promotion_banner_for_newsletter_subscription': ['off', 'on'],
  FFP_3768_hotels_cpc_ads: ['off', 'on'],
  FFP_3768_offers_cpc_ads: ['off', 'on'],
  FFP_3680_hotline_sticky_bar: ['off', 'on'],
  FFP_3943_hotels_adition_ads: ['off', 'on'],
  FFP_3943_offers_adition_ads: ['off', 'on'],
  FFP_5733_hotel_content_api: ['off', 'on'],
  FFP_4024_bloomreach_push_notifications: ['off', 'on'],
  FFP_4165_cashback_voucher: ['off', 'on'],
  FFP_4129_app_promotion_layer: ['off', 'visible', 'disguised'],
  FFP_4073_searchform_behavior: ['A', 'B', 'C'],
  FBO_2918_static_scarcity_elements_IBE3: ['off', 'on'],
  FBO_2919_static_scarcity_elements_IBE4_5: ['off', 'on'],
  FBO_3080_dynamic_scarcity_elements_hotellist: ['off', 'on'],
  FBO_3328_dynscarcity_offerlist_checkout: ['off', 'on', 'default'],
  FPO_5918_find_platform_195: ['off', 'on'],
  FPO_5979_find_platform_195_SEA_Hotellist_Top: ['off', 'on'],
  FFP_4467_upselling_offers: ['off', 'on'],
  FFP_4523_promo_hotellist: ['off', 'on'],
  'FSE-3287_last_searches': ['off', 'on'],
  FSE_3458_show_airport_suggestions: ['off', 'on'],
  FSE_3060_new_datepicker: ['off', 'on'],
  FSE_3296_show_vacations: ['off', 'on'],
  FSE_3299_top_regions_as_list: ['a', 'b', 'c'],
  FPO_5968_find_inline_platform_195: ['off', 'on'],
  FPO_5970_find_inline_platform_195_SEA: ['off', 'on'],
  FFP_18_alternative_flights: ['off', 'on', 'on_2'],
  FBO_2956_price_cache: ['off', 'on'],
  CAP_280_split_exit_layer: ['default', 'off', 'on', 'honeepot'],
  CAP_292_search_form_sidebar_layout: ['default', 'off', 'on'],
  CAP_324_cross_borders_sales: [
    'off',
    'on_with_price',
    'on_without_price',
    'ibe3_only_with_price',
    'ibe3_only_without_price',
    'v1_with_price',
    'v1_without_price',
    'v2_without_price',
  ],
  CAP_310_map_split_view: ['default', 'off', 'on'],
  CAP_310_reorganised_tab_row: ['default', 'off', 'on'],
  FFP_4783_new_hotel_box: ['off', 'on', 'on_2', 'on_3'],
  FFP_4895_find_sponsored_hotel_platform_195: ['off', 'on', 'default'],
  FBO_3023_amadeustrustedreviews: ['off', 'on', 'default'],
  FPO_5871_find_skyscraper_hotellist_platform_195: ['off', 'on', 'default'],
  FPO_6207_find_skyscraper_hotellist_platform_195_SEA: ['off', 'on', 'default'],
  FPO_6208_find_skyscraper_offerlist_platform_195_SEA: ['off', 'on', 'default'],
  FPO_5872_find_skyscraper_offerlist_platform_195: ['off', 'on', 'default'],
  FFP_4890_qa_service: ['off', 'on'],
  FSE_3513_allow_empty_searchform_submit: ['controlgroup', 'variation'],
  FBO_3197_picturegallery: ['off', 'on'],
  FFP_5014_find_sponsored_to_platform_195: ['default', 'off', 'on'],
  FPO_6430_wishlist_feature_toggle: ['off', 'on'],
  FFP_5186_ama_recommendation_percentage: ['default', 'off', 'on'],
  FFP_4050_browser_back_hoteldetailslayer_close: ['default', 'off', 'on'],
  FBO_3463_paypal: ['off', 'on'],
  FBO_3647_review_AIsummary: ['default', 'off', 'variation1', 'variation2'],
  FFP_5811_omnichannel_livechat: ['default', 'off', 'on'],
} as const satisfies Record<
  string,
  Array<LooseString<'on' | 'off' | 'default'>>
>;

export type SplitName = keyof typeof splits;

export const splitNames = Object.keys(splits);

export {config} from '@/config/generated';
